import { modalActions, userActions } from '@/actions';
import Button, { ButtonProps } from '@/components/elements/forms/buttons/Button/Button';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { SCREEN_NAME } from '@/constants/analytics';
import { MEGA_MENU_NAVIGATION_FEATURE_FLAG_PLACE_ONLY } from '@/constants/experimentConstants';
import { isIPhone, promiseWrapper } from '@/helpers';
import { getScreenNameFromPath } from '@/helpers/analyticsTS';
import { loginTrackingSession } from '@/helpers/loginTrackingSession';
import { openIosKeyboard } from '@/helpers/utilsTS';
import { _s } from '@/locale';
import { loginServices } from '@/services';
import { LoginSignupFlowInitiatedFrom, Tracking } from '@/types/analytics';
import { ComponentProps, createContext, ReactNode, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import isEmail from 'validator/lib/isEmail';
import { useAppDispatch, useAppSelector } from '.';

type EmailState = {
  value: string;
  error: boolean;
  submitting: boolean;
  isChecked: boolean;
  isRegistered: boolean;
};

type EmailAction =
  | { type: 'SET_EMAIL_VALUE'; payload: { value: string } }
  | { type: 'SET_EMAIL_ERROR'; payload: { error: boolean } }
  | { type: 'SET_EMAIL_SUBMITTING'; payload: { submitting: boolean } }
  | { type: 'SET_EMAIL_IS_MP_CHECKED'; payload: { isChecked: boolean } }
  | { type: 'SET_EMAIL_IS_REGISTERED'; payload: { isRegistered: boolean } };

const emailReducer = (state: EmailState, action: EmailAction): EmailState => {
  switch (action.type) {
    case 'SET_EMAIL_VALUE': {
      const { value } = action.payload;
      return { ...state, isChecked: false, isRegistered: false, value };
    }
    case 'SET_EMAIL_ERROR': {
      const { error } = action.payload;
      return { ...state, error };
    }
    case 'SET_EMAIL_SUBMITTING': {
      const { submitting } = action.payload;
      return { ...state, submitting };
    }
    case 'SET_EMAIL_IS_MP_CHECKED': {
      const { isChecked } = action.payload;
      return { ...state, isChecked };
    }
    case 'SET_EMAIL_IS_REGISTERED': {
      const { isRegistered } = action.payload;
      return { ...state, isRegistered };
    }
  }
};

export type View = 'requestToken' | 'verifyOtp' | 'verifySuccess' | 'createAccount';

async function requestOTPCode(
  email: string,
  triggerSource: LoginSignupFlowInitiatedFrom,
  redirectUrl: string,
  signal?: AbortSignal,
) {
  await loginServices.requestOtp({
    email,
    triggerSource,
    redirectUrl,
    signal,
  });
}

function LoginButton(
  props: Pick<ButtonProps, 'label' | 'size'> & {
    initiatedFrom: LoginSignupFlowInitiatedFrom;
    trackPromptEvent?: boolean;
  },
) {
  const dispatch = useAppDispatch();
  const loginContext = useContext(LoginContext);
  const flags = useAppSelector((state) => state.flags);
  const location = useLocation();
  const isPlacePage = getScreenNameFromPath(location.pathname) === SCREEN_NAME.COMPANY_DETAILS;
  const { initiatedFrom, trackPromptEvent, ...buttonProps } = props;

  const handleOnClick = () => {
    let isMegaMenuFeature = false;

    if (isPlacePage) {
      isMegaMenuFeature = flags[MEGA_MENU_NAVIGATION_FEATURE_FLAG_PLACE_ONLY]?.value === 'on';
    } else {
      isMegaMenuFeature = true;
    }

    if (isMegaMenuFeature) {
      loginContext.onOpen(initiatedFrom);
      return;
    }
    /**
     * Remove login modal once mega menu is rolled out
     */
    dispatch(modalActions.login({ show: true, loginContext, initiatedFrom }));
  };

  useEffect(() => {
    if (!trackPromptEvent) return;
    if (!loginTrackingSession.isActive()) {
      loginTrackingSession.start(initiatedFrom);
    } else if (loginTrackingSession.getInitiatedFrom() !== initiatedFrom) {
      loginTrackingSession.start(initiatedFrom, { force: true });
    }

    loginTrackingSession.track({ step: 'prompted', flow: 'login' });
  }, []);

  return <Button {...buttonProps} onClick={handleOnClick} />;
}

const useLoginManager = (): {
  flow: Tracking.LoginSignupFlow['properties']['flow'];
  email: EmailState;
  show: boolean;
  view: View;
  user: any;
  updateView: (view: View) => void;
  handleEmailSubmit: ({ redirectUrl }: { redirectUrl: string }) => void;
  handleLogin: (code: string[]) => Promise<boolean>;
  updateEmail: (email: string) => void;
  onOpen: (initiatedFrom: LoginSignupFlowInitiatedFrom) => void;
  onClose: (cancelTrackingSession?: boolean) => void;
  clearLoginState: () => void;
  handleEmailResendClicked: (redirectUrl: string) => void;
  renderLoginButton: (props: ComponentProps<typeof LoginButton>) => ReactNode;
  onAbortServerRequest: () => void;
} => {
  const [email, dispatch] = useReducer(emailReducer, {
    value: '',
    error: false,
    submitting: false,
    isChecked: false,
    isRegistered: false,
  });
  const [show, setShow] = useState(false);
  const [view, setView] = useState<View>('requestToken');
  const user = useSelector((state: any) => state.users)?.user;
  const storeDispatch = useDispatch();
  const abortControllerRef = useRef<AbortController | null>(null);

  const flow: Tracking.LoginSignupFlow['properties']['flow'] =
    email.isChecked && !email.isRegistered ? 'sign-up' : 'login';

  const handleEmailSubmit = async ({ redirectUrl }: { redirectUrl: string }) => {
    dispatch({ type: 'SET_EMAIL_SUBMITTING', payload: { submitting: true } });

    /**
     * Avoid sending server request if email does not pass basic validation
     */
    if (email.error) {
      return;
    }

    /**
     * Create new AbortController or reuse existing abort controller
     * to cancel ongoing requests if user cancels the flow during server request
     */
    if (abortControllerRef.current?.signal?.aborted || !abortControllerRef.current) {
      abortControllerRef.current = new AbortController();
    }

    /**
     * Check if email is registred or not if not already checked to determine if
     * the user should proceed with login or sign-up flow
     */
    if (!email.isChecked) {
      loginTrackingSession.track({ step: 'verification', flow: 'login' });

      const { error } = await promiseWrapper(
        loginServices.isEmailRegistered(email.value, abortControllerRef.current.signal),
      );

      dispatch({ type: 'SET_EMAIL_IS_MP_CHECKED', payload: { isChecked: true } });

      if (error) {
        /**
         * Check if request was aborted, do nothing
         */
        if (error?.name === 'CanceledError') {
          return;
        }

        /**
         * Check if error is 404, meaning email is not registered
         * and switch to create account view
         */
        if (error?.response?.status === 404) {
          dispatch({ type: 'SET_EMAIL_IS_REGISTERED', payload: { isRegistered: false } });
          setView('createAccount');
          return;
        }

        /**
         * Other errors, will show error message on email input since email.error is set
         * and do not proceed
         */
        return;
      } else {
        dispatch({ type: 'SET_EMAIL_IS_REGISTERED', payload: { isRegistered: true } });
      }
    }

    if (email.isChecked && !email.isRegistered) {
      loginTrackingSession.track({ step: 'create_account', flow: 'sign-up' });
    }

    if (isIPhone()) {
      openIosKeyboard();
    }

    const source = loginTrackingSession.getInitiatedFrom();

    const { error } = await promiseWrapper(
      requestOTPCode(email.value, source, redirectUrl, abortControllerRef.current.signal),
    );

    /**
     * Request aborted, do nothing
     */
    if (error?.name === 'CanceledError') {
      return;
    }

    if (error) {
      loginTrackingSession.track({ step: 'failed', flow });
      toast(
        ({ closeToast }) => (
          <Snackbar
            type="danger"
            label={_s('loginModal.error.message')}
            action={
              <button
                onClick={() => {
                  closeToast();
                  setView('requestToken');
                }}>
                {_s('loginModal.error.cta')}
              </button>
            }
          />
        ),
        { autoClose: false },
      );
    }

    setView('verifyOtp');
  };

  const handleEmailResendClicked = (redirectUrl: string) => {
    loginTrackingSession.track({ step: 'resend_code', flow });

    const source = loginTrackingSession.getInitiatedFrom();

    requestOTPCode(email.value, source, redirectUrl).catch(() => {
      toast(
        ({ closeToast }) => (
          <Snackbar
            type="danger"
            label={_s('loginModal.error.message')}
            action={
              <button
                onClick={() => {
                  closeToast();
                  setView('requestToken');
                }}>
                {_s('loginModal.error.cta')}
              </button>
            }
          />
        ),
        { autoClose: false },
      );
    });
  };

  const handleLogin = async (code: string[]): Promise<boolean> => {
    if (abortControllerRef.current?.signal?.aborted || !abortControllerRef.current) {
      abortControllerRef.current = new AbortController();
    }

    loginTrackingSession.track({ step: 'code_submitted', flow });

    const { data, error } = await promiseWrapper(
      loginServices.login({ email: email.value, code: code.join('') }, abortControllerRef.current.signal),
    );

    if (error?.name === 'CanceledError') {
      return false;
    }

    const success = !Boolean(error);

    // @ts-ignore
    storeDispatch(userActions.finishLogin({ user: data?.user, success: !Boolean(error) }));

    if (success) {
      loginTrackingSession.track({ step: flow === 'sign-up' ? 'correct_code' : 'successfull', flow });

      /**
       * End the login tracking session here for 'login' flow only,
       * as for 'sign-up' flow, it will be ended after the account
       * creation is completed.
       */
      if (flow === 'login') {
        loginTrackingSession.end();
      }
    } else {
      const step = error?.response?.status === 401 ? 'wrong_code' : 'failed';
      loginTrackingSession.track({ step, flow });
      return false;
    }

    return true;
  };

  const updateEmail = (_email: string) => {
    dispatch({ type: 'SET_EMAIL_VALUE', payload: { value: _email } });
    dispatch({ type: 'SET_EMAIL_SUBMITTING', payload: { submitting: false } });
    dispatch({ type: 'SET_EMAIL_IS_MP_CHECKED', payload: { isChecked: false } });
  };

  const updateView = (view: View) => {
    if (view === 'verifyOtp' && email.error) setView('requestToken');
    setView(view);
  };

  const clearLoginState = () => {
    dispatch({ type: 'SET_EMAIL_VALUE', payload: { value: '' } });
    dispatch({ type: 'SET_EMAIL_ERROR', payload: { error: false } });
    dispatch({ type: 'SET_EMAIL_SUBMITTING', payload: { submitting: false } });
    dispatch({ type: 'SET_EMAIL_IS_MP_CHECKED', payload: { isChecked: false } });
    setView('requestToken');
  };

  const handleOnClose = (cancelTrackingSession = true) => {
    handleAbortServerRequest();

    if (cancelTrackingSession) {
      loginTrackingSession.track({ step: 'cancelled', flow });
      loginTrackingSession.end();
    }
    setShow(false);
    clearLoginState();
  };

  const handleOnOpen = (initiatedFrom: LoginSignupFlowInitiatedFrom) => {
    setShow(true);

    if (!loginTrackingSession.isActive()) {
      loginTrackingSession.start(initiatedFrom);
    } else if (loginTrackingSession.getInitiatedFrom() !== initiatedFrom) {
      loginTrackingSession.start(initiatedFrom, { force: true });
    }

    loginTrackingSession.track({ step: 'initiated', flow });
  };

  useEffect(() => {
    dispatch({ type: 'SET_EMAIL_ERROR', payload: { error: !isEmail(email.value) } });
  }, [email.value]);

  const handleAbortServerRequest = () => {
    abortControllerRef.current?.abort();
  };

  return {
    flow,
    show,
    email,
    view,
    user,
    updateEmail,
    updateView,
    handleEmailSubmit,
    handleLogin,
    onOpen: handleOnOpen,
    onClose: handleOnClose,
    onAbortServerRequest: handleAbortServerRequest,
    clearLoginState,
    handleEmailResendClicked,
    renderLoginButton: (props) => {
      return <LoginButton {...props} />;
    },
  };
};

export type LoginContextType = ReturnType<typeof useLoginManager>;

export const LoginContext = createContext<LoginContextType>({
  flow: 'login',
  show: false,
  email: {
    value: '',
    error: false,
    submitting: false,
    isChecked: false,
    isRegistered: false,
  },
  view: 'requestToken',
  user: {},
  onOpen: () => {},
  onClose: () => {},
  updateView: () => {},
  handleEmailSubmit: () => {},
  handleLogin: () => Promise.resolve(true),
  updateEmail: () => {},
  clearLoginState: () => {},
  handleEmailResendClicked: () => {},
  renderLoginButton: () => <></>,
  onAbortServerRequest: () => {},
});

export const LoginProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return <LoginContext.Provider value={useLoginManager()}>{children}</LoginContext.Provider>;
};
